import { escapeCsvCell } from "./csvFileHelpers";
import { getDueDatePart } from "./dueDateTime";
import { formatTenantsDisplay } from "./rentalLeaseTenants";

/**
 * CSV columns aligned with the Rental Leases spreadsheet + optional asset_id.
 * Import headers accept friendly labels or snake_case (see findLeaseCsvColumnIndex).
 */
export const RENTAL_LEASE_CSV_COLUMN_IDS = [
  "property_address",
  "tenants",
  "landlords",
  "status",
  "start_date",
  "end_date",
  "rent_amount",
  "security_deposit",
  "pet_deposit",
  "asset_id",
];

export const RENTAL_LEASE_CSV_HEADER_LINE = RENTAL_LEASE_CSV_COLUMN_IDS.join(",");

/** Friendly header aliases → column id (normalized: lowercased, spaces→underscores). */
export const RENTAL_LEASE_CSV_HEADER_ALIASES = {
  property_address: ["property_address", "propertyaddress", "address", "home", "home_address"],
  tenants: ["tenants", "tenant", "tenant(s)"],
  landlords: ["landlords", "landlord", "landlord(s)"],
  status: ["status"],
  start_date: ["start_date", "startdate", "start"],
  end_date: ["end_date", "enddate", "end"],
  rent_amount: ["rent_amount", "rentamount", "rent", "monthly_rent", "monthlyrent"],
  security_deposit: ["security_deposit", "securitydeposit", "security"],
  pet_deposit: ["pet_deposit", "petdeposit", "pet"],
  asset_id: ["asset_id", "assetid", "asset-id", "property_id", "propertyid"],
};

/**
 * @param {string[]} headersNormalized - lowercased headers with spaces → _
 * @param {string} columnId
 * @returns {number}
 */
export function findLeaseCsvColumnIndex(headersNormalized, columnId) {
  const aliases = RENTAL_LEASE_CSV_HEADER_ALIASES[columnId] || [columnId];
  const headers = (headersNormalized || []).map((h) => String(h || "").replace(/[()]/g, ""));
  for (const alias of aliases) {
    const key = String(alias).replace(/[()]/g, "");
    const idx = headers.findIndex((h) => h === key);
    if (idx >= 0) return idx;
  }
  return -1;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string[]}
 */
function rentalLeaseRecordToCsvCells(row) {
  return RENTAL_LEASE_CSV_COLUMN_IDS.map((colId) => {
    switch (colId) {
      case "start_date":
      case "end_date":
        return getDueDatePart(row?.[colId]) || "";
      case "rent_amount":
      case "security_deposit":
      case "pet_deposit": {
        const amt = row?.[colId];
        return amt != null && amt !== "" && Number.isFinite(Number(amt)) ? String(Number(amt)) : "";
      }
      case "status":
        return row?.status != null && String(row.status).trim() !== ""
          ? String(row.status).trim()
          : "Upcoming";
      case "tenants": {
        const display = formatTenantsDisplay(row?.tenants);
        return display === "—" ? "" : display;
      }
      default:
        return row?.[colId] != null ? String(row[colId]).trim() : "";
    }
  });
}

/**
 * @param {Record<string, unknown>[]} rows
 * @returns {string}
 */
export function buildRentalLeaseCsvExportText(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const lines = [
    RENTAL_LEASE_CSV_HEADER_LINE,
    ...list.map((row) => rentalLeaseRecordToCsvCells(row).map(escapeCsvCell).join(",")),
  ];
  return lines.join("\r\n");
}
